import { EditorSelection, type ChangeSpec, type StateCommand } from "@codemirror/state";

/** List-aware Tab / Shift-Tab.
 *
 *  A flat "add two spaces" Tab is wrong for markdown: a nested item has to be
 *  indented to its PARENT'S CONTENT COLUMN, which depends on the parent's
 *  marker. Under `- one` that column is 2, but under `1. one` it is 3 and under
 *  `10. one` it is 4. Indenting an ordered item by two spaces therefore produced
 *  a lazy continuation line rather than a child list — the numbers kept
 *  restarting at the same level and nothing nested at all.
 *
 *  These commands compute the column from the item above and set the line's
 *  indent to it exactly. They return false when the caret is not in a list item,
 *  so `indentWithTab` still handles ordinary indentation everywhere else. */

/** A list line split into its parts: leading whitespace, the marker itself, and
 *  the gap before the content. `null` for a line that is not a list item. */
export interface ListLine {
  indent: string;
  marker: string;
  gap: string;
  /** Column at which this item's CONTENT starts — where a child must indent to. */
  contentCol: number;
  ordered: boolean;
}

const LIST_RE = /^(\s*)([-*+]|\d{1,9}[.)])(\s+)/;

export function parseListLine(text: string): ListLine | null {
  const m = LIST_RE.exec(text);
  if (!m) return null;
  const [, indent = "", marker = "", gap = ""] = m;
  return {
    indent,
    marker,
    gap,
    contentCol: indent.length + marker.length + gap.length,
    ordered: /\d/.test(marker),
  };
}

/** The column a child of the nearest eligible ancestor should sit at, or null
 *  when this item cannot indent any further.
 *
 *  Markdown (and every outliner) allows an item to go at most ONE level deeper
 *  than the item above it — the first item of a list has no parent to nest
 *  under, and skipping levels would produce a list nobody can render. So the
 *  search walks up for the closest preceding item that is not already deeper
 *  than this one, and returns its content column. */
export function nextIndentCol(lines: readonly string[], index: number): number | null {
  const self = parseListLine(lines[index] ?? "");
  if (!self) return null;
  for (let i = index - 1; i >= 0; i--) {
    const text = lines[i] ?? "";
    if (!text.trim()) break; // a blank line ends the run — nothing to nest under
    const prev = parseListLine(text);
    if (!prev) break;
    if (prev.indent.length <= self.indent.length) {
      // Already a child of this one? Then there is no deeper level available.
      return prev.contentCol > self.indent.length ? prev.contentCol : null;
    }
  }
  return null;
}

/** The column this item should fall back to when dedented: the content column of
 *  the nearest ancestor shallower than its own indent, or 0 at the top level. */
export function prevIndentCol(lines: readonly string[], index: number): number | null {
  const self = parseListLine(lines[index] ?? "");
  if (!self || self.indent.length === 0) return null;
  for (let i = index - 1; i >= 0; i--) {
    const text = lines[i] ?? "";
    if (!text.trim()) break;
    const prev = parseListLine(text);
    if (!prev) break;
    if (prev.indent.length < self.indent.length) return prev.indent.length;
  }
  return 0;
}

/** The number an ordered item should carry once it sits at column `col`: one
 *  more than the nearest preceding sibling at that exact column, or 1 when it
 *  has none.
 *
 *  Without this, indenting `2.` under `1.` left a nested list literally starting
 *  at two — so the level rendered as `ii.` and the numbering of the level it had
 *  just left carried on into its own child. Restarting is what makes Enter+Tab
 *  produce `1.` then `i.` rather than `1.` then `ii.`. */
export function renumberAt(lines: readonly string[], index: number, col: number): number {
  for (let i = index - 1; i >= 0; i--) {
    const text = lines[i] ?? "";
    if (!text.trim()) break;
    const prev = parseListLine(text);
    if (!prev) break;
    if (prev.indent.length < col) break; // left this level — no sibling above
    if (prev.indent.length !== col) continue; // a deeper descendant; keep looking
    if (!prev.ordered) break;
    const n = parseInt(prev.marker, 10);
    return Number.isFinite(n) ? n + 1 : 1;
  }
  return 1;
}

/** Re-indent every list line touched by the selection, using `pick` to choose
 *  the new column. Lines that are not list items are left to the next binding. */
function reindent(pick: (lines: string[], i: number) => number | null): StateCommand {
  return ({ state, dispatch }) => {
    const lines = state.doc.toString().split("\n");
    const changes: ChangeSpec[] = [];
    let moved = 0;
    for (const range of state.selection.ranges) {
      const first = state.doc.lineAt(range.from).number;
      const last = state.doc.lineAt(range.to).number;
      for (let n = first; n <= last; n++) {
        const line = state.doc.line(n);
        const parsed = parseListLine(line.text);
        if (!parsed) continue;
        const col = pick(lines, n - 1);
        if (col == null || col === parsed.indent.length) continue;
        // Rewrite the indent, and for an ordered item its number too: moving
        // between levels makes it either the first of a new list or the next
        // sibling of an existing one.
        const marker = parsed.ordered
          ? `${renumberAt(lines, n - 1, col)}${parsed.marker.slice(-1)}`
          : parsed.marker;
        changes.push({
          from: line.from,
          to: line.from + parsed.indent.length + parsed.marker.length,
          insert: " ".repeat(col) + marker,
        });
        // Keep `lines` in step so a multi-line selection nests relative to the
        // indentation its earlier lines just received, not their old one.
        lines[n - 1] =
          " ".repeat(col) + marker + line.text.slice(parsed.indent.length + parsed.marker.length);
        moved++;
      }
    }
    if (!moved) return false;
    dispatch(
      state.update({
        changes,
        // `EditorSelection.map` through the changes keeps the caret on the same
        // character rather than at a fixed offset that the re-indent moved.
        selection: EditorSelection.create(
          state.selection.ranges.map((r) => EditorSelection.range(r.anchor, r.head)),
          state.selection.mainIndex,
        ).map(state.changes(changes)),
        scrollIntoView: true,
        userEvent: "input.indent",
      }),
    );
    return true;
  };
}

/** Tab inside a list item: nest it under the item above. */
export const indentListItem: StateCommand = reindent(nextIndentCol);

/** Shift-Tab inside a list item: lift it back out one level. */
export const dedentListItem: StateCommand = reindent(prevIndentCol);
